import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// Network charge intake (billing PR #7, 2026-10-30) - SHADOW and DARK.
//   /billing/intake              upload a network clinic's batch PDF; every uploaded batch
//   /billing/intake/:batchId     one batch: each visit read from it, its proposed charge lines,
//                                the checks, WestStar's price, the charges Raintree already has
//                                on that visit, and accept / edit / reject per line
// The URL carries the portal's batch id only. Behind the server's billing gate like the rest of
// Billing. Nothing here changes Raintree: the reviewed list is what staff key into Raintree, and
// the next morning the portal compares (the stage-2 shadow score).
// Nov 1 (the first real batches): the list and a batch being read update by themselves (K.5); a batch has a
// Back to batches button (K.6); a visit whose case or charges sit at another clinic says where (K.8).
// Nov 1 update: the Billing bar on top (components/BillingNav.jsx); each visit's "page N" opens the batch PDF at
// that page, so the reviewer can hold the HCFA next to what was read (K.9).

const POLL_MS = 10000;   // while a batch is being read, re-ask every 10 s; stops once nothing is being read
// Matt's #72 review: each re-ask is an audited read, so skip it while the tab is hidden, and stop after an error
// (an expired session, say) instead of asking forever - a manual Refresh or a successful load starts it again.

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function usDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

function usTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

const STATUS = {
  uploaded: ["Uploaded", "bg-slate-100 text-slate-700"],
  extracting: ["Reading…", "bg-sky-100 text-sky-800"],
  extracted: ["Ready to review", "bg-emerald-100 text-emerald-800"],
  failed: ["Failed", "bg-rose-100 text-rose-800"],
};

function StatusPill({ status }) {
  const [label, cls] = STATUS[status] || [status, "bg-slate-100 text-slate-700"];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

function Note({ children, tone = "slate" }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800"
    : tone === "rose" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-slate-200 bg-white text-slate-600";
  return <div className={`mt-4 rounded-lg border p-4 text-sm ${cls}`}>{children}</div>;
}

// ── the list + upload ──────────────────────────────────────────────────

function Upload({ onDone }) {
  const [locs, setLocs] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [loc, setLoc] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/billing/intake/locations")
      .then((res) => { if (live) { setLocs(res.data.locations || []); setEnabled(!!res.data.enabled); } })
      .catch(() => live && setLocs([]));
    return () => { live = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!loc || !file) return;
    setBusy(true);
    setMsg(null);
    const form = new FormData();
    form.append("location_code", loc);
    form.append("file", file);
    try {
      const res = await api.post("/billing/intake/batches", form);
      setMsg(res.data.status === "failed"
        ? { tone: "rose", text: `Stored as batch ${res.data.batch_id}, but it couldn't be sent for reading: ${res.data.error}. Use Resend on the batch.` }
        : { tone: "slate", text: `Batch ${res.data.batch_id} uploaded. It's being read now (a few minutes for a big batch) - the list below updates by itself.` });
      setFile(null);
      e.target.reset();
      onDone();
    } catch (err) {
      setMsg({ tone: "rose", text: errText(err, "The upload didn't go through.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="intake-upload">
      <div className="text-sm font-semibold text-slate-800">Upload a batch</div>
      <p className="text-xs text-slate-500">One PDF as the clinic sent it: cover sheet, HCFAs, chart notes. The same file can only be uploaded once.</p>
      {!enabled && locs !== null && (
        <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Reading batches isn't switched on yet, so uploads are refused for now.</div>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="text-sm text-slate-700">
          <div className="text-xs text-slate-500">Clinic</div>
          <select value={loc} onChange={(e) => setLoc(e.target.value)} className="mt-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" data-testid="intake-clinic">
            <option value="">Pick a network clinic…</option>
            {(locs || []).map((l) => <option key={l.location_code} value={l.location_code}>{l.name} ({l.location_code})</option>)}
          </select>
        </label>
        <label className="text-sm text-slate-700">
          <div className="text-xs text-slate-500">Batch PDF</div>
          <input type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 text-sm" data-testid="intake-file" />
        </label>
        <button type="submit" disabled={busy || !loc || !file} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40" data-testid="intake-submit">
          {busy ? "Uploading…" : "Upload"}
        </button>
      </div>
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </form>
  );
}

function BatchList() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.get("/billing/intake/batches")
      .then((res) => { setD(res.data); setError(null); })
      .catch((e) => setError(errText(e, "Couldn't load the batches.")));
  }, []);
  useEffect(() => { load(); }, [load]);
  const reading = !!d && d.batches.some((b) => b.status === "extracting" && !b.stale);
  useEffect(() => {
    if (!reading || error) return undefined;
    const t = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [reading, error, load]);

  return (
    <div>
      <Upload onDone={load} />
      {error && <Note tone="amber">{error}</Note>}
      {!d && !error && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
      {d && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm" data-testid="intake-batches">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Batch</th><th className="px-3 py-2">Clinic</th><th className="px-3 py-2">Uploaded</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Visits</th>
                <th className="px-3 py-2 text-right">Lines</th><th className="px-3 py-2 text-right">To review</th>
              </tr>
            </thead>
            <tbody>
              {d.batches.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No batch uploaded yet.</td></tr>
              )}
              {d.batches.map((b) => (
                <tr key={b.batch_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><Link to={`/billing/intake/${b.batch_id}`} className="font-medium text-sky-700 hover:underline">#{b.batch_id}</Link></td>
                  <td className="px-3 py-2">{b.clinic || b.location_code}</td>
                  <td className="px-3 py-2">{usTime(b.uploaded_at)}<div className="text-xs text-slate-500">{b.uploaded_by_username}</div></td>
                  <td className="px-3 py-2"><StatusPill status={b.status} />{b.stale ? <div className="text-xs text-amber-700">No answer - open to resend</div> : null}</td>
                  <td className="px-3 py-2 text-right">{b.visits}{b.visits ? <span className="text-xs text-slate-500"> ({b.visits_matched} found in Raintree)</span> : null}</td>
                  <td className="px-3 py-2 text-right">{b.lines}</td>
                  <td className="px-3 py-2 text-right">{b.pending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── one batch ──────────────────────────────────────────────────────────

const RT = {
  keyed: ["In Raintree", "bg-emerald-50 text-emerald-800"],
  keyed_other_units: ["In Raintree, other units", "bg-amber-50 text-amber-800"],
  not_keyed: ["Not in Raintree yet", "bg-slate-100 text-slate-600"],
};

function Checks({ c }) {
  const out = [];
  if (c.price_known === false) out.push(["No WestStar price for this code", "bg-rose-50 text-rose-800"]);
  if (c.amount_vs_price === "differs") out.push(["Clinic's amount differs from WestStar's price", "bg-amber-50 text-amber-800"]);
  if (c.in_note === false) out.push(["Not in the chart note", "bg-rose-50 text-rose-800"]);
  if (c.units_vs_minutes && !c.units_vs_minutes.ok) {
    out.push([`Timed units ${c.units_vs_minutes.timed_units}, the note's minutes allow ${c.units_vs_minutes.expected}`, "bg-amber-50 text-amber-800"]);
  }
  if (c.raintree && RT[c.raintree]) {
    const [t, cls] = RT[c.raintree];
    out.push([c.raintree_at && c.raintree !== "not_keyed" ? `${t} (under ${c.raintree_at})` : t, cls]);
  }
  return (
    <div className="flex flex-wrap gap-1">
      {out.map(([t, cls]) => <span key={t} className={`rounded px-1.5 py-0.5 text-xs ${cls}`}>{t}</span>)}
    </div>
  );
}

function EditRow({ line, onSave, onCancel }) {
  const [f, setF] = useState({
    cpt_code: line.final_cpt_code || line.cpt_code || "",
    modifiers: line.final_modifiers || line.modifiers || "",
    units: line.final_units || line.units || "",
    amount: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-2 text-xs">
      <label>CPT<input value={f.cpt_code} onChange={set("cpt_code")} className="ml-1 w-20 rounded border px-1 py-0.5" /></label>
      <label>Modifiers<input value={f.modifiers} onChange={set("modifiers")} className="ml-1 w-24 rounded border px-1 py-0.5" /></label>
      <label>Units<input value={f.units} onChange={set("units")} className="ml-1 w-14 rounded border px-1 py-0.5" /></label>
      <label>Amount<input value={f.amount} onChange={set("amount")} placeholder="WestStar price" className="ml-1 w-28 rounded border px-1 py-0.5" /></label>
      <button type="button" onClick={() => onSave(f)} className="rounded bg-slate-900 px-2 py-1 text-white">Save</button>
      <button type="button" onClick={onCancel} className="rounded border px-2 py-1">Cancel</button>
    </div>
  );
}

function clinicName(c) {
  return c ? `${c.name || c.location_code} (${c.location_code})` : "";
}

function Visit({ v, act, editing, setEditing, clinic, openPdf }) {
  const pending = v.lines.filter((l) => l.review_status === "pending").map((l) => l.line_id);
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="intake-visit">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">{v.patient_name || "Name not read"}</div>
          <div className="text-xs text-slate-500">
            Born {usDate(v.date_of_birth)} · Visit {usDate(v.date_of_service)} · Claim {v.claim_number || "—"} · Injury {usDate(v.date_of_injury)}
            {v.source_page ? (
              <>
                {" · "}
                <button type="button" onClick={() => openPdf(v.source_page)} className="text-sky-700 hover:underline"
                        title="Opens the batch PDF at this page in a new tab" data-testid="intake-open-page">
                  page {v.source_page}
                </button>
              </>
            ) : ""}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Chart note: {v.note_minutes != null ? `${v.note_minutes} timed minutes` : "minutes not read"}{v.note_cpts ? ` · codes ${v.note_cpts}` : ""}
          </div>
        </div>
        <div className="text-right text-xs">
          {v.match_status === "matched" && v.case_id && (
            <Link to={`/billing/case/${v.case_id}`} className="text-sky-700 hover:underline">Open the case in the portal</Link>
          )}
          {v.match_status === "matched" && !v.case_id && v.case_at && (
            <div data-testid="intake-case-elsewhere">
              <div className="text-amber-700">Case under {clinicName(v.case_at)}</div>
              <div className="text-slate-500">Care at {clinic}</div>
              {v.case_at.case_id ? (
                <Link to={`/billing/case/${v.case_at.case_id}`} className="text-sky-700 hover:underline">Open that case in the portal</Link>
              ) : null}
            </div>
          )}
          {v.match_status === "matched" && !v.case_id && !v.case_at && !v.no_case_anywhere && (
            <span className="text-amber-700" data-testid="intake-no-case-here">Matched - check the case by hand</span>
          )}
          {v.match_status === "matched" && v.other_clinic_that_day && v.other_clinic_that_day.length > 0 && (
            <div className="text-amber-700" data-testid="intake-other-clinic">
              Keyed at another clinic that day: {v.other_clinic_that_day.map(clinicName).join(", ")} - check
            </div>
          )}
          {v.match_status === "matched" && v.no_case_anywhere && (
            <span className="text-amber-700" data-testid="intake-no-case-anywhere">In Raintree, but no case at any clinic - open one before keying</span>
          )}
          {v.match_status === "ambiguous" && <span className="text-amber-700">More than one patient matches - check by hand</span>}
          {v.match_status === "unmatched" && (
            <span className="text-rose-700" data-testid="intake-not-found">Not found in Raintree - search by name and birth date</span>
          )}
          {pending.length > 0 && (
            <div className="mt-2">
              <button type="button" onClick={() => act(pending, "accept")} className="rounded bg-emerald-700 px-2 py-1 text-white">Accept the {pending.length} pending</button>
            </div>
          )}
        </div>
      </div>
      <table className="mt-3 min-w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
          <tr><th className="py-1">Code</th><th className="py-1">Units</th><th className="py-1 text-right">Clinic</th>
            <th className="py-1 text-right">WestStar</th><th className="py-1 pl-3">Checks</th><th className="py-1">Review</th></tr>
        </thead>
        <tbody>
          {v.lines.map((l) => (
            <tr key={l.line_id} className="border-t border-slate-100 align-top">
              <td className="py-1.5">{l.cpt_code || "—"}{l.modifiers ? <span className="text-xs text-slate-500"> {l.modifiers}</span> : null}</td>
              <td className="py-1.5">{l.units ?? "—"}</td>
              <td className="py-1.5 text-right">{money(l.charge_amount)}</td>
              <td className="py-1.5 text-right">{money(l.proposed_amount)}{l.unit_price ? <div className="text-xs text-slate-500">{money(l.unit_price)} / unit</div> : null}</td>
              <td className="py-1.5 pl-3"><Checks c={l.checks || {}} /></td>
              <td className="py-1.5">
                <div className="text-xs">
                  {l.review_status === "pending" && <span className="text-slate-600">Pending</span>}
                  {l.review_status === "accepted" && <span className="text-emerald-700">Accepted</span>}
                  {l.review_status === "edited" && <span className="text-emerald-700">Edited: {l.final_cpt_code} {l.final_modifiers || ""} × {l.final_units} = {money(l.final_amount)}</span>}
                  {l.review_status === "rejected" && <span className="text-rose-700">Rejected</span>}
                </div>
                <div className="mt-1 flex gap-1 text-xs">
                  {l.review_status !== "accepted" && <button type="button" onClick={() => act([l.line_id], "accept")} className="rounded border px-1.5 py-0.5">Accept</button>}
                  <button type="button" onClick={() => setEditing(l.line_id)} className="rounded border px-1.5 py-0.5">Edit</button>
                  {l.review_status !== "rejected" && <button type="button" onClick={() => act([l.line_id], "reject")} className="rounded border px-1.5 py-0.5">Reject</button>}
                  {l.review_status !== "pending" && <button type="button" onClick={() => act([l.line_id], "reset")} className="rounded border px-1.5 py-0.5">Undo</button>}
                </div>
                {editing === l.line_id && (
                  <EditRow line={l} onCancel={() => setEditing(null)} onSave={(f) => act([l.line_id], "edit", f)} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {v.raintree.length > 0 && (
        <div className="mt-2 text-xs text-slate-500">
          Raintree has on this visit{v.keyed_at && v.keyed_at.length ? `, keyed under ${v.keyed_at.map(clinicName).join(", ")}` : ""}: {v.raintree.map((k) => `${k.cpt_code} × ${k.units} (${money(k.billed_amount)})`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function Batch({ batchId }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/billing/intake/batches/${batchId}`)
      .then((res) => { setD(res.data); setError(null); })
      .catch((e) => setError(errText(e, "Couldn't load this batch.")));
  }, [batchId]);
  useEffect(() => { load(); }, [load]);
  const reading = !!d && d.status === "extracting" && !d.stale;
  useEffect(() => {
    if (!reading || error) return undefined;
    const t = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [reading, error, load]);

  const act = async (ids, action, final) => {
    setMsg(null);
    try {
      await api.post(`/billing/intake/batches/${batchId}/review`, { line_ids: ids, action, final });
      setEditing(null);
      load();
    } catch (e) {
      setMsg(errText(e, "That didn't save."));
    }
  };

  const resend = async () => {
    setMsg(null);
    try {
      await api.post(`/billing/intake/batches/${batchId}/resend`);
      load();
    } catch (e) {
      setMsg(errText(e, "Couldn't resend."));
    }
  };

  // The batch PDF is fetched once per visit to the page and kept as a local blob URL; "page 7" opens it at that
  // page (#page=N, read by the browser's PDF viewer). Nothing leaves the browser; the file stays the portal's copy.
  const pdfUrl = useRef(null);
  // Matt's #72 review: free the PDF when the reviewer leaves this batch (or opens another), so it isn't kept for the
  // whole portal tab and one batch's PDF can never open under another.
  useEffect(() => () => {
    if (pdfUrl.current) {
      URL.revokeObjectURL(pdfUrl.current);
      pdfUrl.current = null;
    }
  }, [batchId]);
  const openPdf = async (page) => {
    try {
      if (!pdfUrl.current) {
        const res = await api.get(`/billing/intake/batches/${batchId}/file`, { responseType: "blob" });
        pdfUrl.current = URL.createObjectURL(res.data);
      }
      const n = Number(page);
      window.open(Number.isInteger(n) && n > 0 ? `${pdfUrl.current}#page=${n}` : pdfUrl.current, "_blank", "noopener");
    } catch (e) {
      setMsg("Couldn't open the PDF.");
    }
  };

  if (error) return <Note tone="amber">{error}</Note>;
  if (!d) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const s = d.score || {};
  // K.8: the lines keyed under the sister location, on their own, so the clinic's own keying isn't blended in
  const sisterNames = (d.sisters || []).map(clinicName).join(", ") || "a sister location";
  const atSister = (x) => (x && x.agree_at_sister ? `; ${x.agree_at_sister} keyed under ${sisterNames}` : "");
  const lines = d.visits.reduce((n, v) => n + v.lines.length, 0);
  const pending = d.visits.reduce((n, v) => n + v.lines.filter((l) => l.review_status === "pending").length, 0);
  return (
    <div>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <StatusPill status={d.status} />
        <span className="text-slate-700">{d.clinic || d.location_code}</span>
        <span className="text-slate-500">uploaded {usTime(d.uploaded_at)} by {d.uploaded_by_username}</span>
        {d.page_count ? <span className="text-slate-500">{d.page_count} pages</span> : null}
        <button type="button" onClick={() => openPdf()} className="rounded border border-slate-300 px-2 py-1 text-xs" data-testid="intake-open-pdf">Open the PDF</button>
        {d.status === "extracting" && <button type="button" onClick={load} className="rounded border border-slate-300 px-2 py-1 text-xs">Refresh</button>}
        {(d.status === "failed" || d.stale) && <button type="button" onClick={resend} className="rounded bg-slate-900 px-2 py-1 text-xs text-white">Resend for reading</button>}
      </div>
      {d.status === "extracting" && !d.stale && <Note>The batch is being read. This page updates by itself; a big batch takes a few minutes.</Note>}
      {d.stale && <Note tone="amber">No answer from the reader for over 30 minutes. Resend it for reading.</Note>}
      {d.status === "failed" && <Note tone="rose">Reading failed: {d.error || "no reason given"}.</Note>}
      {msg && <Note tone="rose">{msg}</Note>}
      {d.status === "extracted" && (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="intake-score">
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm"><div className="text-xs text-slate-500">Visits</div>{d.visits.length}</div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm"><div className="text-xs text-slate-500">Proposed lines</div>{lines} ({pending} to review)</div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm">
            <div className="text-xs text-slate-500">Shadow score (lines that match what staff keyed in Raintree)</div>
            {s.on_keyed_visits || (s.as_proposed && s.as_proposed.on_keyed_visits) ? (
              <div>
                <div>As read: {s.as_proposed ? `${s.as_proposed.agree} of ${s.as_proposed.on_keyed_visits}` : "-"}{atSister(s.as_proposed)}</div>
                <div>As reviewed: {s.agree} of {s.on_keyed_visits}{atSister(s)}</div>
              </div>
            ) : "Nothing keyed in Raintree for these visits yet"}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm"><div className="text-xs text-slate-500">In Raintree, not proposed</div>{s.raintree_only || 0}</div>
        </div>
      )}
      {d.visits.map((v) => <Visit key={v.visit_no} v={v} act={act} editing={editing} setEditing={setEditing} clinic={d.clinic || d.location_code} openPdf={openPdf} />)}
    </div>
  );
}

export default function BillingIntake() {
  const { user } = useAuth();
  const { batchId } = useParams();
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;
  return (
    <div className="mx-auto max-w-6xl p-6" data-testid="billing-intake">
      <BillingNav current="intake" />
      <div className="text-sm text-slate-500">
        <Link to="/billing" className="text-sky-700 hover:underline">Billing</Link> / <Link to="/billing/intake" className="text-sky-700 hover:underline">Network intake</Link>
        {batchId ? ` / Batch #${batchId}` : ""}
      </div>
      {batchId && (
        <Link to="/billing/intake" className="mt-2 inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="intake-back">
          ← Back to batches
        </Link>
      )}
      <h1 className="mt-1 text-xl font-semibold text-slate-900">{batchId ? `Batch #${batchId}` : "Network charge intake"}</h1>
      <p className="text-sm text-slate-500">
        Proposed charge lines read from a network clinic's batch. Review them here, then key them into Raintree as usual -
        nothing here changes Raintree. The next morning the portal compares what was keyed.
      </p>
      {batchId ? <Batch batchId={batchId} /> : <BatchList />}
    </div>
  );
}
